import { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertTriangle, ChevronDown, ChevronUp, RotateCcw, Warehouse, Store, AlertOctagon, Package, DollarSign } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@/components/ui/collapsible';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { syncAPI } from '@/services/api';
import { formatDate } from '@/utils/dateFormat';

const SyncStatusPage = () => {
  const [syncJobs, setSyncJobs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [manualOnlyFilter, setManualOnlyFilter] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Job details dialog
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobDetailsOpen, setJobDetailsOpen] = useState(false);
  const [retrying, setRetrying] = useState(false);
  
  // Expanded rows for location details
  const [expandedRows, setExpandedRows] = useState({});
  
  // Manual bulk sync dialogs
  const [productSyncDialogOpen, setProductSyncDialogOpen] = useState(false);
  const [pricingSyncDialogOpen, setPricingSyncDialogOpen] = useState(false);
  const [bulkSyncing, setBulkSyncing] = useState(false);
  
  const { toast } = useToast();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [jobsRes, statsRes] = await Promise.all([
        syncAPI.listJobs({
          job_status: statusFilter !== 'all' ? statusFilter : undefined,
          sync_type: typeFilter !== 'all' ? typeFilter : undefined,
          requires_manual: manualOnlyFilter ? true : undefined,
          page,
          page_size: 20
        }),
        syncAPI.getStats()
      ]);
      
      setSyncJobs(jobsRes.data?.items || []);
      setTotalPages(jobsRes.data?.total_pages || 1);
      setStats(statsRes.data);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to fetch sync data', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, [statusFilter, typeFilter, manualOnlyFilter, page, toast]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case 'failed':
        return <XCircle className="w-4 h-4 text-red-500" />;
      case 'partial':
        return <AlertTriangle className="w-4 h-4 text-amber-500" />;
      case 'in_progress':
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case 'manual_required':
        return <AlertOctagon className="w-4 h-4 text-red-600" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-emerald-100 text-emerald-700',
      failed: 'bg-red-100 text-red-700',
      partial: 'bg-amber-100 text-amber-700',
      in_progress: 'bg-blue-100 text-blue-700',
      pending: 'bg-gray-100 text-gray-700',
      manual_required: 'bg-red-200 text-red-800 font-semibold'
    };
    return (
      <Badge className={styles[status] || styles.pending}>
        {status === 'manual_required' ? 'MANUAL REQUIRED' : status?.replace('_', ' ').toUpperCase()}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const styles = {
      price_update: 'bg-purple-100 text-purple-700',
      product_update: 'bg-blue-100 text-blue-700',
      product_create: 'bg-emerald-100 text-emerald-700',
      offer_update: 'bg-orange-100 text-orange-700'
    };
    return (
      <Badge variant="outline" className={styles[type] || ''}>
        {type?.replace('_', ' ')}
      </Badge>
    );
  };

  const toggleRowExpand = (jobId) => {
    setExpandedRows(prev => ({ ...prev, [jobId]: !prev[jobId] }));
  };

  const handleRetry = async (jobId, locationType = null, locationIds = null) => {
    setRetrying(true);
    try {
      await syncAPI.retryJob(jobId, locationType, locationIds);
      toast({ title: 'Success', description: 'Retry initiated' });
      fetchData();
      if (jobDetailsOpen && selectedJob?.id === jobId) {
        const res = await syncAPI.getJob(jobId);
        setSelectedJob(res.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to retry sync', variant: 'destructive' });
    } finally {
      setRetrying(false);
    }
  };

  const handleManualSync = async (jobId) => {
    setRetrying(true);
    try {
      await syncAPI.manualSync(jobId);
      toast({ title: 'Success', description: 'Manual sync initiated' });
      fetchData();
      if (jobDetailsOpen && selectedJob?.id === jobId) {
        const res = await syncAPI.getJob(jobId);
        setSelectedJob(res.data);
      }
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to initiate manual sync', variant: 'destructive' });
    } finally {
      setRetrying(false);
    }
  };

  // Bulk sync all products
  const handleBulkProductSync = async () => {
    setBulkSyncing(true);
    try {
      await syncAPI.bulkProductSync();
      toast({ title: 'Success', description: 'All products sync initiated successfully' });
      setProductSyncDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to initiate product sync', variant: 'destructive' });
    } finally {
      setBulkSyncing(false);
    }
  };

  // Bulk sync all pricing
  const handleBulkPricingSync = async () => {
    setBulkSyncing(true);
    try {
      await syncAPI.bulkPricingSync();
      toast({ title: 'Success', description: 'All product pricing sync initiated successfully' });
      setPricingSyncDialogOpen(false);
      fetchData();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to initiate pricing sync', variant: 'destructive' });
    } finally {
      setBulkSyncing(false);
    }
  };

  const openJobDetails = async (job) => {
    try {
      const res = await syncAPI.getJob(job.id);
      setSelectedJob(res.data);
      setJobDetailsOpen(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load job details', variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 lg:p-8 bg-gray-50 min-h-screen">
      <Toaster />
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Sync Status</h1>
          <p className="text-gray-500">Monitor data synchronization to warehouses and stores</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button 
            onClick={() => setProductSyncDialogOpen(true)} 
            className="bg-blue-600 hover:bg-blue-700 text-white"
            data-testid="sync-all-products-btn"
          >
            <Package className="w-4 h-4 mr-2" /> Sync All Products
          </Button>
          <Button 
            onClick={() => setPricingSyncDialogOpen(true)} 
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="sync-all-pricing-btn"
          >
            <DollarSign className="w-4 h-4 mr-2" /> Sync All Pricing
          </Button>
          <Button onClick={fetchData} variant="outline" className="border-gray-300">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6">
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-gray-500">Total Jobs</p>
              <p className="text-2xl font-bold text-gray-800">{stats.total_jobs}</p>
            </CardContent>
          </Card>
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-gray-500">Today</p>
              <p className="text-2xl font-bold text-blue-600">{stats.today_syncs}</p>
            </CardContent>
          </Card>
          <Card className="bg-emerald-50 border-emerald-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-emerald-600">Completed</p>
              <p className="text-2xl font-bold text-emerald-700">{stats.completed}</p>
            </CardContent>
          </Card>
          <Card className="bg-blue-50 border-blue-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-blue-600">In Progress</p>
              <p className="text-2xl font-bold text-blue-700">{stats.in_progress}</p>
            </CardContent>
          </Card>
          <Card className="bg-amber-50 border-amber-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-amber-600">Partial</p>
              <p className="text-2xl font-bold text-amber-700">{stats.partial}</p>
            </CardContent>
          </Card>
          <Card className="bg-red-50 border-red-200 shadow-sm">
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-red-600">Failed</p>
              <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
            </CardContent>
          </Card>
          <Card className={`shadow-sm ${stats.manual_required > 0 ? 'bg-red-100 border-red-300' : 'bg-gray-50 border-gray-200'}`}>
            <CardContent className="pt-4 pb-3">
              <p className="text-sm text-red-700">Manual Required</p>
              <p className="text-2xl font-bold text-red-800">{stats.manual_required || 0}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-4 mb-6">
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-white border-gray-300">
            <SelectValue placeholder="All Status" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="pending">Pending</SelectItem>
            <SelectItem value="in_progress">In Progress</SelectItem>
            <SelectItem value="completed">Completed</SelectItem>
            <SelectItem value="partial">Partial</SelectItem>
            <SelectItem value="failed">Failed</SelectItem>
            <SelectItem value="manual_required">Manual Required</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={typeFilter} onValueChange={(v) => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44 bg-white border-gray-300">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="price_update">Price Update</SelectItem>
            <SelectItem value="product_update">Product Update</SelectItem>
            <SelectItem value="product_create">Product Create</SelectItem>
            <SelectItem value="offer_update">Offer Update</SelectItem>
          </SelectContent>
        </Select>

        <Button
          variant={manualOnlyFilter ? "default" : "outline"}
          onClick={() => { setManualOnlyFilter(!manualOnlyFilter); setPage(1); }}
          className={manualOnlyFilter ? "bg-red-500 hover:bg-red-600" : "border-gray-300"}
        >
          <AlertOctagon className="w-4 h-4 mr-2" />
          Manual Required Only
        </Button>
      </div>

      {/* Sync Jobs Table */}
      <Card className="bg-white border-gray-200 shadow-sm">
        <CardHeader className="pb-3 border-b border-gray-100">
          <CardTitle className="text-lg text-gray-800">Sync Jobs</CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Loading...</div>
          ) : syncJobs.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No sync jobs found
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50 border-gray-200">
                    <TableHead className="w-8"></TableHead>
                    <TableHead className="text-gray-700">Type</TableHead>
                    <TableHead className="text-gray-700">Entity</TableHead>
                    <TableHead className="text-gray-700">Change</TableHead>
                    <TableHead className="text-gray-700">Initiated</TableHead>
                    <TableHead className="text-gray-700 text-center">Warehouses</TableHead>
                    <TableHead className="text-gray-700 text-center">Stores</TableHead>
                    <TableHead className="text-gray-700 text-center">Status</TableHead>
                    <TableHead className="text-gray-700 text-center">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {syncJobs.map((job) => (
                    <Collapsible key={job.id} open={expandedRows[job.id]} asChild>
                      <>
                        <TableRow className="border-gray-200 hover:bg-gray-50">
                          <TableCell>
                            <CollapsibleTrigger asChild>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => toggleRowExpand(job.id)}>
                                {expandedRows[job.id] ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                              </Button>
                            </CollapsibleTrigger>
                          </TableCell>
                          <TableCell>{getTypeBadge(job.sync_type)}</TableCell>
                          <TableCell>
                            <p className="font-medium text-gray-800 text-sm">{job.entity_name}</p>
                            <p className="text-gray-400 text-xs">{job.entity_type}</p>
                          </TableCell>
                          <TableCell className="max-w-xs">
                            <p className="text-sm text-gray-600 truncate" title={job.change_summary}>
                              {job.change_summary}
                            </p>
                          </TableCell>
                          <TableCell>
                            <p className="text-sm text-gray-600">{formatDate(job.initiated_at)}</p>
                            <p className="text-xs text-gray-400">{job.initiated_by_name}</p>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Warehouse className="w-3 h-3 text-gray-400" />
                              <span className="text-emerald-600 font-medium">{job.synced_warehouses || 0}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-gray-600">{job.total_warehouses || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Store className="w-3 h-3 text-gray-400" />
                              <span className="text-emerald-600 font-medium">{job.synced_stores || 0}</span>
                              <span className="text-gray-400">/</span>
                              <span className="text-gray-600">{job.total_stores || 0}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex items-center justify-center gap-1">
                              {getStatusIcon(job.status)}
                              {getStatusBadge(job.status)}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <div className="flex justify-center gap-1">
                              <Button 
                                variant="ghost" 
                                size="sm" 
                                className="h-8 px-2 text-gray-600"
                                onClick={() => openJobDetails(job)}
                              >
                                Details
                              </Button>
                              {job.requires_manual_sync && (
                                <Button 
                                  variant="default" 
                                  size="sm" 
                                  className="h-8 px-2 bg-red-500 hover:bg-red-600 text-white"
                                  onClick={() => handleManualSync(job.id)}
                                  disabled={retrying}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" /> Manual Sync
                                </Button>
                              )}
                              {!job.requires_manual_sync && (job.status === 'failed' || job.status === 'partial') && (
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 px-2 text-blue-600"
                                  onClick={() => handleRetry(job.id)}
                                  disabled={retrying}
                                >
                                  <RotateCcw className="w-3 h-3 mr-1" /> Retry
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                        <CollapsibleContent asChild>
                          <TableRow className="bg-gray-50 border-gray-200">
                            <TableCell colSpan={9} className="p-4">
                              <Tabs defaultValue="warehouses" className="w-full">
                                <TabsList className="grid w-full max-w-md grid-cols-2 mb-4">
                                  <TabsTrigger value="warehouses" className="flex items-center gap-2">
                                    <Warehouse className="w-4 h-4" /> Warehouses ({job.total_warehouses || 0})
                                  </TabsTrigger>
                                  <TabsTrigger value="stores" className="flex items-center gap-2">
                                    <Store className="w-4 h-4" /> Stores ({job.total_stores || 0})
                                  </TabsTrigger>
                                </TabsList>
                                <TabsContent value="warehouses">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {job.warehouse_sync_status?.map((wh) => (
                                      <div 
                                        key={wh.warehouse_id} 
                                        className={`p-3 rounded border ${
                                          wh.status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                                          wh.status === 'manual_required' ? 'bg-red-100 border-red-300' :
                                          wh.status === 'failed' ? 'bg-red-50 border-red-200' :
                                          'bg-gray-100 border-gray-200'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          {getStatusIcon(wh.status)}
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate">{wh.warehouse_name}</p>
                                            <p className="text-xs text-gray-500">{wh.warehouse_code}</p>
                                          </div>
                                        </div>
                                        {wh.retry_count > 0 && (
                                          <p className="text-xs text-amber-600 mt-1">Retries: {wh.retry_count}/6</p>
                                        )}
                                        {wh.error_message && (
                                          <p className="text-xs text-red-600 mt-1 truncate" title={wh.error_message}>{wh.error_message}</p>
                                        )}
                                      </div>
                                    ))}
                                    {(!job.warehouse_sync_status || job.warehouse_sync_status.length === 0) && (
                                      <p className="col-span-full text-center text-gray-400 py-4">No warehouses</p>
                                    )}
                                  </div>
                                </TabsContent>
                                <TabsContent value="stores">
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                                    {job.store_sync_status?.map((store) => (
                                      <div 
                                        key={store.store_id} 
                                        className={`p-3 rounded border ${
                                          store.status === 'completed' ? 'bg-emerald-50 border-emerald-200' :
                                          store.status === 'manual_required' ? 'bg-red-100 border-red-300' :
                                          store.status === 'failed' ? 'bg-red-50 border-red-200' :
                                          'bg-gray-100 border-gray-200'
                                        }`}
                                      >
                                        <div className="flex items-center gap-2">
                                          {getStatusIcon(store.status)}
                                          <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 truncate">{store.store_name}</p>
                                            <p className="text-xs text-gray-500">{store.store_code}</p>
                                          </div>
                                        </div>
                                        {store.retry_count > 0 && (
                                          <p className="text-xs text-amber-600 mt-1">Retries: {store.retry_count}/6</p>
                                        )}
                                        {store.error_message && (
                                          <p className="text-xs text-red-600 mt-1 truncate" title={store.error_message}>{store.error_message}</p>
                                        )}
                                      </div>
                                    ))}
                                    {(!job.store_sync_status || job.store_sync_status.length === 0) && (
                                      <p className="col-span-full text-center text-gray-400 py-4">No stores</p>
                                    )}
                                  </div>
                                </TabsContent>
                              </Tabs>
                            </TableCell>
                          </TableRow>
                        </CollapsibleContent>
                      </>
                    </Collapsible>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center gap-2 mt-4">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    Previous
                  </Button>
                  <span className="px-4 py-2 text-sm text-gray-600">
                    Page {page} of {totalPages}
                  </span>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Job Details Dialog */}
      <Dialog open={jobDetailsOpen} onOpenChange={setJobDetailsOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-800">Sync Job Details</DialogTitle>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              {/* Job Summary */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="font-medium">{selectedJob.sync_type?.replace('_', ' ')}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-500">Status</p>
                  {getStatusBadge(selectedJob.status)}
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-500">Entity</p>
                  <p className="font-medium">{selectedJob.entity_name}</p>
                </div>
                <div className="bg-gray-50 p-3 rounded">
                  <p className="text-sm text-gray-500">Initiated By</p>
                  <p className="font-medium">{selectedJob.initiated_by_name}</p>
                </div>
              </div>

              {/* Timing & Retry Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-sm text-blue-700">
                    <strong>Initiated:</strong> {formatDate(selectedJob.initiated_at)}
                  </p>
                  {selectedJob.completed_at && (
                    <p className="text-sm text-blue-700">
                      <strong>Completed:</strong> {formatDate(selectedJob.completed_at)}
                    </p>
                  )}
                </div>
                {selectedJob.next_retry_at && !selectedJob.requires_manual_sync && (
                  <div className="bg-amber-50 p-3 rounded">
                    <p className="text-sm text-amber-700">
                      <strong>Next Auto-Retry:</strong> {formatDate(selectedJob.next_retry_at)}
                    </p>
                  </div>
                )}
                {selectedJob.requires_manual_sync && (
                  <div className="bg-red-100 p-3 rounded">
                    <p className="text-sm text-red-700 font-semibold">
                      ⚠️ Manual sync required - exceeded 6 retry attempts
                    </p>
                  </div>
                )}
              </div>

              {/* Change Summary */}
              <div className="bg-purple-50 p-3 rounded">
                <p className="text-sm text-purple-700">
                  <strong>Change:</strong> {selectedJob.change_summary}
                </p>
              </div>

              {/* Warehouse Status */}
              <div>
                <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                  <Warehouse className="w-4 h-4" /> Warehouse Sync Status ({selectedJob.synced_warehouses}/{selectedJob.total_warehouses})
                </h4>
                <div className="max-h-48 overflow-y-auto border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Warehouse</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Retries</TableHead>
                        <TableHead>Synced At</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedJob.warehouse_sync_status?.map((wh) => (
                        <TableRow key={wh.warehouse_id} className="border-gray-200">
                          <TableCell className="font-medium">{wh.warehouse_name}</TableCell>
                          <TableCell>{wh.warehouse_code}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {getStatusIcon(wh.status)}
                              <span className="text-sm">{wh.status}</span>
                            </div>
                          </TableCell>
                          <TableCell>{wh.retry_count || 0}/6</TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {wh.synced_at ? formatDate(wh.synced_at) : '-'}
                          </TableCell>
                          <TableCell className="text-sm text-red-600 max-w-xs truncate">
                            {wh.error_message || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>

              {/* Store Status */}
              <div>
                <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                  <Store className="w-4 h-4" /> Store Sync Status ({selectedJob.synced_stores}/{selectedJob.total_stores})
                </h4>
                <div className="max-h-48 overflow-y-auto border rounded">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-gray-50">
                        <TableHead>Store</TableHead>
                        <TableHead>Code</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead>Retries</TableHead>
                        <TableHead>Synced At</TableHead>
                        <TableHead>Error</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {selectedJob.store_sync_status?.map((store) => (
                        <TableRow key={store.store_id} className="border-gray-200">
                          <TableCell className="font-medium">{store.store_name}</TableCell>
                          <TableCell>{store.store_code}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              {getStatusIcon(store.status)}
                              <span className="text-sm">{store.status}</span>
                            </div>
                          </TableCell>
                          <TableCell>{store.retry_count || 0}/6</TableCell>
                          <TableCell className="text-sm text-gray-600">
                            {store.synced_at ? formatDate(store.synced_at) : '-'}
                          </TableCell>
                          <TableCell className="text-sm text-red-600 max-w-xs truncate">
                            {store.error_message || '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </div>
          )}
          
          <DialogFooter>
            {selectedJob && selectedJob.requires_manual_sync && (
              <Button 
                onClick={() => handleManualSync(selectedJob.id)}
                disabled={retrying}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {retrying ? 'Processing...' : 'Manual Sync All Failed'}
              </Button>
            )}
            {selectedJob && !selectedJob.requires_manual_sync && (selectedJob.status === 'failed' || selectedJob.status === 'partial') && (
              <Button 
                onClick={() => handleRetry(selectedJob.id)}
                disabled={retrying}
                className="bg-blue-500 hover:bg-blue-600 text-white"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                {retrying ? 'Processing...' : 'Retry Failed Locations'}
              </Button>
            )}
            <Button variant="outline" onClick={() => setJobDetailsOpen(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Product Sync Confirmation Dialog */}
      <Dialog open={productSyncDialogOpen} onOpenChange={setProductSyncDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <Package className="w-5 h-5 text-blue-600" />
              Sync All Products
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              This will sync all product information (name, barcode, category, brand, HSN code, etc.) to all stores and warehouses.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 my-4">
            <p className="text-sm text-blue-800">
              <strong>What will be synced:</strong>
            </p>
            <ul className="text-sm text-blue-700 mt-2 space-y-1 list-disc list-inside">
              <li>Product names and descriptions</li>
              <li>SKU and barcodes</li>
              <li>Categories and brands</li>
              <li>HSN codes and tax groups</li>
              <li>Product variants and attributes</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProductSyncDialogOpen(false)} disabled={bulkSyncing}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkProductSync}
              disabled={bulkSyncing}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {bulkSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...
                </>
              ) : (
                <>
                  <Package className="w-4 h-4 mr-2" /> Start Product Sync
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Pricing Sync Confirmation Dialog */}
      <Dialog open={pricingSyncDialogOpen} onOpenChange={setPricingSyncDialogOpen}>
        <DialogContent className="bg-white border-gray-200 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-800 flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-purple-600" />
              Sync All Pricing
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              This will sync all product pricing information to all stores and warehouses.
            </DialogDescription>
          </DialogHeader>
          
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 my-4">
            <p className="text-sm text-purple-800">
              <strong>What will be synced:</strong>
            </p>
            <ul className="text-sm text-purple-700 mt-2 space-y-1 list-disc list-inside">
              <li>MRP (Maximum Retail Price)</li>
              <li>Selling prices</li>
              <li>Purchase prices</li>
              <li>Store-specific pricing</li>
              <li>Active offers and discounts</li>
            </ul>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setPricingSyncDialogOpen(false)} disabled={bulkSyncing}>
              Cancel
            </Button>
            <Button 
              onClick={handleBulkPricingSync}
              disabled={bulkSyncing}
              className="bg-purple-600 hover:bg-purple-700 text-white"
            >
              {bulkSyncing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Syncing...
                </>
              ) : (
                <>
                  <DollarSign className="w-4 h-4 mr-2" /> Start Pricing Sync
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SyncStatusPage;
